import type { RequestAnalyticsContext } from '@/analytics/context';
import type { ClientProductDisplay } from '@/utils/clientProductDisplay';
import { request } from '@umijs/max';

export interface CompanyRequestOptions {
  signal?: AbortSignal;
}

export async function getCompanyById(
  id: number,
  analyticsContext?: RequestAnalyticsContext,
  options?: CompanyRequestOptions,
): Promise<any> {
  return request(`/company/${id}`, {
    method: 'GET',
    ...(analyticsContext ? { analyticsContext } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export async function getCompanyPeople(
  id: number,
  analyticsContext?: RequestAnalyticsContext,
  options?: CompanyRequestOptions,
): Promise<any> {
  return request(`/company/${id}/people`, {
    method: 'GET',
    ...(analyticsContext ? { analyticsContext } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export interface CompanyLocation {
  company: string | null;
  name: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  facilityType: string | null;
  phone: string | null;
  source: string | null;
  confidence: number | null;
}

export async function getCompanyLocations(
  foragerId: number,
  analyticsContext?: RequestAnalyticsContext,
  options?: CompanyRequestOptions,
): Promise<CompanyLocation[]> {
  return request(`/company/${foragerId}/locations`, {
    method: 'GET',
    ...(analyticsContext ? { analyticsContext } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export interface Product {
  id: number;
  name: string;
  product_image_guid?: string | null;
  product_image_url?: string | null;
  product_images?: Array<string | Record<string, any>> | Record<string, any> | null;
  primary_image_link?: string | null;
  image_thumbnail?: string | null;
  nutrition_image?: string | null;
  ingredient_image?: string | null;
  brand?: string | null;
  packaging_type: string | null;
  application?: string | null;
  category: string | null;
  sub_category?: string | null;
  country_of_origin: string;
  marketed_by?: string | null;
  price_usd?: number | null;
  unit_pack_size?: string | null;
  packaging_units?: string | null;
  fragrances?: string | null;
  location_of_manufacture?: string | null;
  packaging_material?: string | null;
  matched_packaging_types?: string[];
  matched_packaging_materials?: string[];
  is_match?: boolean;
  evaluation_eligible?: boolean;
  evaluation_match_context?: {
    blend_group_ids: number[];
    client_product_ids: number[];
    ingredient_ids: number[];
  };
  matched_ingredients?: Product['ingredients'];
  matched_ingredient_ids?: number[];
  matched_client_products?: ClientProductDisplay[];
  matched_client_categories?: Array<{ id: number; name: string }>;
  matched_category_groups?: Array<{
    category_id: number;
    category_name: string;
    group_id?: number;
    group_name: string;
  }>;
  marketer_employee_count?: number | null;
  manufacturer_employee_count?: number | null;
  marketer: {
    id: number;
    name: string;
    foragerId: string;
  } | null;
  manufacturer: {
    id: number;
    name: string;
    foragerId: number;
  } | null;
  countries_of_sale: string[];
  country_of_sale?: string | null;
  ingredients: {
    id: number;
    name: string;
    ins: string | null;
    botanical_name: string | null;
    volume: string | null;
  }[];
}

export interface CompanyProductsResponse {
  data: Product[];
  total: number;
  totalAll: number;
  matchedTotal?: number;
  hasMatchCriteria?: boolean;
  take: number;
  skip: number;
}

export interface CompanyIngredientGroupDistributionResponse {
  totalAll: number;
  groupMatchedTotal: number;
  groups: Array<{
    groupId: number;
    groupName: string;
    ingredientCount: number;
    rawMatchedProductCount: number;
    allocatedProductCount: number;
  }>;
}

export async function getCompanyProducts(
  foragerId: number,
  params?: {
    take?: number;
    skip?: number;
    ingredientIds?: number[];
    categoryId?: number;
    useCategoryPortfolio?: boolean;
    productFilters?: Record<string, unknown>;
    rankMatches?: boolean;
    combinePositivePackagingFilters?: boolean;
  },
  analyticsContext?: RequestAnalyticsContext,
  options?: CompanyRequestOptions,
): Promise<CompanyProductsResponse> {
  const productFilters =
    params?.productFilters && Object.keys(params.productFilters).length > 0
      ? JSON.stringify(params.productFilters)
      : undefined;

  const categoryId = Number(params?.categoryId);
  const useCategoryEndpoint =
    params?.useCategoryPortfolio === true || (Number.isInteger(categoryId) && categoryId > 0);

  return request(
    useCategoryEndpoint
      ? `/enterprise/company/${foragerId}/category-products`
      : `/company/${foragerId}/products`,
    {
      method: 'GET',
      ...(analyticsContext ? { analyticsContext } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
      params: {
        take: params?.take,
        skip: params?.skip,
        ...(useCategoryEndpoint
          ? {
              categoryId: Number.isInteger(categoryId) && categoryId > 0 ? categoryId : undefined,
              ...(params?.rankMatches === true ? { rankMatches: true } : {}),
            }
          : {
              ingredientIds: params?.ingredientIds?.join(','),
              productFilters,
              ...(params?.rankMatches === true ? { rankMatches: true } : {}),
              ...(params?.combinePositivePackagingFilters === true
                ? { combinePositivePackagingFilters: true }
                : {}),
            }),
      },
    },
  );
}

export async function getCompanyIngredientGroupDistribution(
  foragerId: number,
  analyticsContext?: RequestAnalyticsContext,
  options?: CompanyRequestOptions,
): Promise<CompanyIngredientGroupDistributionResponse> {
  return request(`/company/${foragerId}/ingredient-group-distribution`, {
    method: 'GET',
    ...(analyticsContext ? { analyticsContext } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}
